import React, {CSSProperties, useState} from 'react'

import ControlLabel from './ControlLabel.js'
import {FieldProps} from './types.js'
import {choiceTokens, getChoiceBoxColors, getChoiceOverlayStyle} from './choiceStyles.js'
import {markFieldRowControl, useFieldRowOffset} from './FormRowContext.js'

// Not a real input event: the toggle is a div, onChange gets a plain object with the new checked value
export interface ToggleChangeEvent {
    target: {checked: boolean}
}

export interface ToggleProps extends FieldProps {
    checked: boolean
    onChange?: (event: ToggleChangeEvent) => void
}

const Toggle = ({disabled, readOnly, checked, label, info, style = {}, onChange}: ToggleProps) => {
    const [focused, setFocused] = useState(false)
    const [hover, setHover] = useState(false)
    const rowOffset = useFieldRowOffset(choiceTokens.height)

    // readOnly ignores clicks and keys like disabled, but is shown normally
    const interactive = !disabled && !readOnly

    const onClick = () => {
        if (interactive) {
            onChange?.({target: {checked: !checked}})
            setFocused(false)
        }
    }

    // preventDefault stops the page from scrolling
    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32) {
            event.preventDefault()

            if (interactive) {
                onChange?.({target: {checked: !checked}})
            }
        }
    }

    const overLayStyle: CSSProperties = {
        ...getChoiceOverlayStyle({highlighted: hover || focused, interactive}),
        marginLeft: choiceTokens.labelGap.toggle
    }

    const trackColors = getChoiceBoxColors({checked, disabled})

    const containerStyle: CSSProperties = {
        position: 'relative',
        display: ' inline-block',
        width: 36,
        height: 20,
        border: `1px solid ${trackColors.border}`,
        borderRadius: 10,
        transition: 'all .1s ease',
        // Off the track is grey, also when enabled
        background: checked ? trackColors.background : choiceTokens.toggleUncheckedTrack,
        outline: 'none'
    }

    const sliderStyle: CSSProperties = {
        width: 16,
        height: 16,
        position: 'absolute',
        top: 1,
        left: checked ? 17 : 2,
        background: !disabled ? choiceTokens.toggleKnob : checked ? trackColors.foreground : choiceTokens.toggleUncheckedDisabledKnob,
        transition: 'all .1s ease',
        borderRadius: '50%'
    }

    const defaultStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
        height: choiceTokens.height,
        ...choiceTokens.margins.toggle,
        ...rowOffset,
        ...style
    }

    if (style && style.width) {
        delete defaultStyle.flex
    }

    return (
        <div style={defaultStyle}>

            {label &&
                <ControlLabel info={info} disabled={disabled}>
                    <div style={{whiteSpace: 'nowrap'}}>{label}</div>
                </ControlLabel>
            }

            <div
                style={overLayStyle}
                onClick={onClick}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                <div
                    tabIndex={disabled ? -1 : 0}
                    role='switch'
                    aria-checked={!!checked}
                    aria-disabled={disabled || undefined}
                    aria-readonly={readOnly || undefined}
                    style={containerStyle}
                    onKeyDown={onKeyDown}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                >
                    <div style={sliderStyle} />
                </div>
            </div>
        </div>
    )
}

export default markFieldRowControl(Toggle)
